import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Text } from 'rebass';
import { Input, Label } from '../Form';
import { useMutation } from '@apollo/client';
import { ContactProfileInput } from '../Profile/types';
import Form from '../Form/Form';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import useNotify from '../../hooks/useNotify';
import { CompanyInput } from './types';
import ContactProfileForm from '../Profile/ContactProfileForm';
import { CREATE_COMPANY, UPDATE_COMPANY } from './CompanyEditModal.queries';
import { Checkbox } from '@rebass/forms';
import { Company } from './CompanyThirdPane.queries';
import WaterSuppliersPicker from '../WaterSupplier/WaterSuppliersPicker';

type CompanyEditModalProps = {
  onClose: () => void;
  onSave?: (company: Company) => void;
} & (
  | {
      action: 'add';
      company?: Partial<CompanyInput>;
    }
  | {
      action: 'edit';
      company?: CompanyInput;
    }
);

const CompanyEditModal: React.FC<CompanyEditModalProps> = ({
  onClose,
  onSave,
  action,
  company,
}) => {
  const [companyInput, setCompanyInput] = useState<CompanyInput>(
    company
      ? {
          profile: company.profile
            ? {
                type: company.profile.type ?? 'COMPANY',
                name: company.profile.name,
                phones: company.profile.phones.map((phone) => ({
                  subType: phone.subType,
                  value: phone.value,
                  extension: phone.extension,
                })),
                emails: company.profile.emails.map((email) => ({
                  subType: email.subType,
                  value: email.value,
                })),
                contactAddressId: company.profile.contactAddressId,
                billingAddressId: company.profile.billingAddressId,
              }
            : {
                type: 'COMPANY',
                name: '',
                phones: [],
                emails: [],
              },
          user: company.user
            ? {
                email: company.user.email,
                password: company.user.password,
              }
            : {
                email: '',
                password: '',
              },
          isBackflowTester: company.isBackflowTester ?? true,
          isWaterSupplier: company.isWaterSupplier ?? false,
          waterSupplierIds: company.waterSupplierIds ?? [],
        }
      : {
          profile: {
            type: 'COMPANY',
            name: '',
            firstName: '',
            lastName: '',
            phones: [],
            emails: [],
          },
          isBackflowTester: true,
          isWaterSupplier: false,
          waterSupplierIds: [],
        }
  );

  const { showError, showSuccess } = useNotify();

  const [createCompany, { loading: createLoading }] = useMutation<
    { createCompany: Company },
    { input: CompanyInput }
  >(CREATE_COMPANY, {
    onError: (error) => {
      showError('Error creating company', error);
      console.error('Error creating company', error);
    },
    onCompleted: (data) => {
      showSuccess('Company added');
      if (data.createCompany && onSave) {
        onSave(data.createCompany);
      }
      onClose();
    },
  });

  const [updateCompany, { loading: updateLoading }] = useMutation<
    { updateCompany: Company },
    { id: string; input: CompanyInput }
  >(UPDATE_COMPANY, {
    onError: (error) => {
      showError('Error updating company', error);
      console.error('Error updating company', error);
    },
    onCompleted: (data) => {
      showSuccess('Company updated');
      if (data.updateCompany && onSave) {
        onSave(data.updateCompany);
      }
      onClose();
    },
  });

  const onSubmit = useCallback(async () => {
    if (!companyInput.profile.name) {
      showError('Please provide a name');
      return;
    }

    if (action === 'add') {
      if (!companyInput.user?.email || !companyInput.user?.password) {
        showError('Please provide login email and password');
        return;
      }
      await createCompany({
        variables: { input: companyInput },
      });
    } else if (action === 'edit') {
      await updateCompany({
        variables: {
          id: company?.profileId as string,
          input: companyInput,
        },
      });
    }
  }, [companyInput, createCompany, company?.profileId, action, updateCompany]);

  const handleCompanyObjChange = (e: any) => {
    const { name, type, checked } = e.target;
    let { value } = e.target;
    if (type === 'checkbox') {
      value = checked;
    }
    setCompanyInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCompanyLoginChange = (e: any) => {
    const { name, value } = e.target;
    setCompanyInput((prevState) => ({
      ...prevState,
      user: {
        ...prevState?.user,
        [name]: value,
      },
    }));
  };

  const onProfileChange = (profile: ContactProfileInput) => {
    setCompanyInput((prevState) => ({
      ...prevState,
      profile: {
        ...prevState.profile,
        ...profile,
      },
    }));
  };

  return (
    <Modal
      title={action === 'add' ? 'Add Company' : 'Edit Company'}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <ContactProfileForm
            onChange={onProfileChange}
            value={companyInput.profile}
            show={['name', 'contactAddressId', 'emails', 'phones']}
          />
          <FormRow>
            <FormColumn>
              <Text mt={3} mb={1} variant="body1">
                {company?.user?.email && 'Update User Login'}
                {!company?.user?.email && 'Primary User Login'}
              </Text>
              <Label>Email</Label>
              <Input
                width="100%"
                type="email"
                value={companyInput?.user?.email || ''}
                onChange={handleCompanyLoginChange}
                name="email"
                autoComplete="off"
              />
              <Label>Password</Label>
              <Input
                width="100%"
                type="password"
                value={companyInput?.user?.password || ''}
                onChange={handleCompanyLoginChange}
                name="password"
                autoComplete="new-password"
              />
            </FormColumn>
            <FormColumn>
              <Text mt={3} mb={1} variant="body1">
                Extensions
              </Text>
              <Label htmlFor="isBackflowTester">
                <Checkbox
                  id="isBackflowTester"
                  name="isBackflowTester"
                  checked={companyInput.isBackflowTester}
                  onChange={handleCompanyObjChange}
                />
                Backflow Tester
              </Label>
              <Label htmlFor="isWaterSupplier">
                <Checkbox
                  id="isWaterSupplier"
                  name="isWaterSupplier"
                  checked={companyInput.isWaterSupplier}
                  onChange={handleCompanyObjChange}
                />
                Water Supplier
              </Label>
            </FormColumn>
          </FormRow>
          {companyInput.isBackflowTester && (
            <FormRow>
              <FormColumn>
                <Text my={1} variant="body1">
                  Water Suppliers
                </Text>
                <WaterSuppliersPicker
                  waterSupplierIds={companyInput?.waterSupplierIds}
                  onChange={(waterSuppliers) => {
                    setCompanyInput((prevState) => ({
                      ...prevState,
                      waterSupplierIds:
                        waterSuppliers?.map(
                          (waterSupplier) => waterSupplier.profileId
                        ) ?? [],
                    }));
                  }}
                />
              </FormColumn>
            </FormRow>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="save"
          onClick={onSubmit}
          disabled={createLoading || updateLoading}
        >
          {action === 'add' ? 'Add' : 'Save'}
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CompanyEditModal.propTypes = {
  onClose: PropTypes.any,
  company: PropTypes.any,
  action: PropTypes.any,
};

export default CompanyEditModal;
